<template>
  <div>
    <loading
      :active.sync="isLoading"
      color="#9de35c"
      loader="bars"
      :width="loadingSize"
      :height="loadingSize"
      :is-full-page="loadingFullPage"
    />
    
    <v-row class="mx-0 mb-2" align="center">
      <v-col cols="auto" class="py-1 py-sm-2">
        <v-btn text @click="goBack">
          <v-icon left>mdi-arrow-left</v-icon> Back to Groups
        </v-btn>
      </v-col>
      <v-spacer />
      <v-col class="py-1 py-sm-2 text-right">
        <app-btn @click="loadHistory">
          <v-icon class="mr-1">mdi-refresh</v-icon> Refresh
        </app-btn>
      </v-col>
    </v-row>

    <!-- Group Info Header -->
    <v-alert v-if="group.name" dense text type="info" class="mb-4">
      <strong>Group:</strong> {{ group.name }} | 
      <strong>Command History</strong>
    </v-alert>

    <!-- Command History Card -->
    <material-card
      icon="mdi-history"
      icon-small
      color="warning"
      title="Command History"
    >
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6" md="4" class="py-2">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              hide-details
              label="Search history"
              single-line
            />
          </v-col>
          <v-col cols="12" sm="6" md="4" class="py-2">
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              label="Filter by Status"
              clearable
              hide-details
            />
          </v-col>
        </v-row>
        <v-divider class="mt-1" />
        
        <v-data-table
          :headers="tableHeaders"
          :items="filteredHistory"
          :search="search"
          :loading="loading"
          :items-per-page="15"
          :footer-props="{
            showFirstLastPage: true,
            'items-per-page-options': [10, 15, 25, 50],
          }"
        >
          <template v-slot:item.type="{ item }">
            <v-chip x-small color="primary" text-color="white">
              {{ item.type }}
            </v-chip>
          </template>
          
          <template v-slot:item.deliveryStatus="{ item }">
            <v-chip
              x-small
              :color="getStatusColor(item.deliveryStatus)"
              text-color="white"
            >
              {{ item.deliveryStatus }}
            </v-chip>
          </template>
          
          <template v-slot:item.deviceInfo="{ item }">
            <div class="device-info">
              <span>{{ item.deviceName || 'Unnamed Device' }}</span>
              <span v-if="item.macAddress" class="mac-address">{{ item.macAddress }}</span>
              <span v-else class="device-id">Device #{{ item.deviceId }}</span>
            </div>
          </template>
          
          <template v-slot:item.deliveryAttempts="{ item }">
            <v-badge
              :content="String(item.deliveryAttempts)"
              :color="item.deliveryAttempts > 1 ? 'warning' : 'success'"
              inline
            />
          </template>
          
          <template v-slot:item.sentAt="{ item }">
            {{ formatDate(item.sentAt) }}
          </template>
          
          <template v-slot:item.acknowledgedAt="{ item }">
            <span v-if="item.acknowledgedAt" class="success--text">
              {{ formatDate(item.acknowledgedAt) }}
            </span>
            <span v-else class="grey--text">Pending</span>
          </template>
          
          <template v-slot:item.expiresAt="{ item }">
            <span v-if="item.expiresAt">{{ formatDate(item.expiresAt) }}</span>
            <span v-else class="grey--text">Never</span>
          </template>
          
          <template v-slot:item.lastDeliveryError="{ item }">
            <v-tooltip v-if="item.lastDeliveryError" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  small
                  color="error"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-alert-circle
                </v-icon>
              </template>
              <span>{{ item.lastDeliveryError }}</span>
            </v-tooltip>
            <span v-else>-</span>
          </template>
        </v-data-table>
      </v-card-text>
    </material-card>

    <!-- Statistics Card -->
    <material-card
      icon="mdi-chart-bar"
      icon-small
      color="info"
      title="Command Statistics"
      class="mt-4"
    >
      <v-card-text>
        <v-row>
          <v-col cols="6" sm="3">
            <div class="stat-item">
              <div class="stat-value">{{ history.length }}</div>
              <div class="stat-label">Total Commands</div>
            </div>
          </v-col>
          <v-col cols="6" sm="3">
            <div class="stat-item">
              <div class="stat-value success--text">{{ acknowledgedCount }}</div>
              <div class="stat-label">Acknowledged</div>
            </div>
          </v-col>
          <v-col cols="6" sm="3">
            <div class="stat-item">
              <div class="stat-value warning--text">{{ pendingCount }}</div>
              <div class="stat-label">Pending</div>
            </div>
          </v-col>
          <v-col cols="6" sm="3">
            <div class="stat-item">
              <div class="stat-value error--text">{{ failedCount }}</div>
              <div class="stat-label">Failed</div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </material-card>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import MaterialCard from '@/components/MaterialCard'
import DeviceGroupsDataService from '@/services/DeviceGroupsDataService'

export default {
  name: 'DeviceGroupHistory',
  components: {
    Loading,
    MaterialCard,
  },
  
  data() {
    return {
      isLoading: false,
      loadingFullPage: true,
      loadingSize: 64,
      loading: false,
      
      groupId: null,
      group: {},
      history: [],
      
      search: '',
      statusFilter: null,
      statusOptions: [
        { text: 'Acknowledged', value: 'Acknowledged' },
        { text: 'Pending', value: 'Pending' },
        { text: 'Failed', value: 'Failed' },
        { text: 'Delivered', value: 'Delivered' },
        { text: 'Queued', value: 'Queued' },
      ],
      
      tableHeaders: [
        { text: 'Command Type', value: 'type', sortable: true },
        { text: 'Status', value: 'deliveryStatus', sortable: true },
        { text: 'Device', value: 'deviceInfo', sortable: false },
        { text: 'Attempts', value: 'deliveryAttempts', sortable: true, align: 'center' },
        { text: 'Sent At', value: 'sentAt', sortable: true },
        { text: 'Acknowledged', value: 'acknowledgedAt', sortable: true },
        { text: 'Expires', value: 'expiresAt', sortable: true },
        { text: 'Error', value: 'lastDeliveryError', sortable: false, align: 'center' },
      ],
    }
  },
  
  computed: {
    filteredHistory() {
      const devices = this.group.effectiveDevices || []
      const byId = new Map(devices.map(device => [String(device.deviceId || device.id), device]))
      const byMac = new Map(devices.filter(device => device.macAddress).map(device => [device.macAddress.toLowerCase(), device]))
      let result = this.history.map(item => {
        const device = byId.get(String(item.deviceId)) || byMac.get((item.macAddress || '').toLowerCase()) || {}
        const deviceName = item.deviceName || device.deviceName || device.name || item.device?.deviceName || item.device?.name || ''
        const macAddress = item.macAddress || device.macAddress || item.device?.macAddress || ''
        return { ...item, deviceName, macAddress, deviceInfo: `${deviceName} ${macAddress} ${item.deviceId || ''}` }
      })
      
      if (this.statusFilter) {
        result = result.filter(item => 
          item.deliveryStatus?.toLowerCase() === this.statusFilter.toLowerCase()
        )
      }
      
      return result
    },
    
    acknowledgedCount() {
      return this.history.filter(h => h.acknowledgedAt).length
    },
    
    pendingCount() {
      return this.history.filter(h => 
        !h.acknowledgedAt && 
        !h.lastDeliveryError &&
        (!h.expiresAt || !this.$format.isPast(h.expiresAt))
      ).length
    },
    
    failedCount() {
      return this.history.filter(h => 
        h.lastDeliveryError || 
        (h.expiresAt && this.$format.isPast(h.expiresAt) && !h.acknowledgedAt)
      ).length
    },
  },
  
  mounted() {
    this.groupId = this.$route.params.id
    this.loadGroup()
    this.loadHistory()
  },
  
  methods: {
    async loadGroup() {
      try {
        const response = await DeviceGroupsDataService.get(this.groupId)
        this.group = response.data || {}
      } catch (error) {
        console.error('Error loading group:', error)
      }
    },
    
    async loadHistory() {
      this.loading = true
      try {
        const response = await DeviceGroupsDataService.getCommandHistory(this.groupId, {
          limit: 200
        })
        this.history = response.data || []
      } catch (error) {
        this.$toast.error('Failed to load command history')
        console.error('Error loading history:', error)
      } finally {
        this.loading = false
      }
    },
    
    getStatusColor(status) {
      const colors = {
        'Acknowledged': 'success',
        'Delivered': 'info',
        'Pending': 'warning',
        'Queued': 'primary',
        'Failed': 'error',
      }
      return colors[status] || 'grey'
    },
    
    goBack() {
      this.$router.push('/device-groups')
    },
    
    formatDate(date) {
      if (!date) return '-'
      // Naive UTC in, local out.
      return this.$format.dateTime(date)
    },
  },
}
</script>

<style scoped>
.device-info {
  display: flex;
  flex-direction: column;
}

.mac-address {
  font-family: monospace;
  font-size: 0.85em;
}

.device-id {
  font-size: 0.85em;
  color: rgba(0, 0, 0, 0.6);
}

.stat-item {
  text-align: center;
  padding: 16px;
}

.stat-value {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
}
</style>
